import * as Separator from '@radix-ui/react-separator'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import imgMonitoring from '@/assets/images/be-guardian/monitoring.jpg'
import preservation from '@/assets/images/be-guardian/preservation-of-be-guardian.webp'
import regeneration from '@/assets/images/be-guardian/regeneration-of-be-guardian.webp'
import circleBird from '@/assets/images/guardian/circle-bird.webp'
import cloud from '@/assets/images/guardian/cloud.webp'
import personLemonCloud from '@/assets/images/guardian/person-lemon-cloud.webp'
import imgDamping from '@/assets/images/services-amortecimento.webp'
import { Skeleton } from '@/components/skeleton'
import type { Indicators, IndicatorsKeysWithoutGeral } from '@/models/guardian'
import api from '@/services/api'

import { IndicatorsCard } from './components/indicators-card'

export function Header() {
  const [indicators, setIndicators] = useState<Indicators | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const abortController = new AbortController()
    const signal = abortController.signal
    async function getIndicators() {
      await api
        .get<Indicators>('/guardianindicators', { signal })
        .then(({ data }) => setIndicators(data))
        .catch((error) => toast.error(error.response.data.message))
        .finally(() => setIsLoading(false))
    }
    getIndicators()

    return () => {
      abortController.abort()
    }
  }, [])

  const imageUrl: Record<keyof IndicatorsKeysWithoutGeral, string> = {
    preservacao: preservation,
    regeneracao: regeneration,
    amortecimento: imgDamping,
    monitoracao: imgMonitoring,
  }

  const titleMap: Record<keyof IndicatorsKeysWithoutGeral, string> = {
    preservacao: 'Preservação',
    regeneracao: 'Regeneração',
    amortecimento: 'Amortecimento',
    monitoracao: 'Monitoração',
  }

  return (
    <div className="relative rounded-2xl bg-zinc-50 p-8">
      <h1 className="banner-title text-bios-title mt-5 mb-10 text-center text-3xl md:mt-10 lg:text-4xl xl:mt-8 2xl:text-5xl">
        Seja um guardião ambiental
      </h1>

      <div
        className={twMerge(
          'flex flex-col items-center justify-center rounded-lg text-center md:flex-row',
          'mx-auto px-10 py-20 md:mb-8 md:w-4/5 lg:mb-16 xl:mb-20 xl:w-1/2 2xl:w-2/5',
          'border border-black/5 bg-white shadow-sm',
        )}
      >
        <div className="text-bios-title">
          {indicators && (
            <p className="text-4xl 2xl:text-5xl">{indicators.geral.projetos}</p>
          )}
          {!indicators && <Skeleton className="mx-auto size-12" />}
          <span className="text-sm">projetos</span>
        </div>

        <Separator.Root className="bg-bios-primary mx-3 my-6 h-px w-1/2 md:mb-auto md:w-12 lg:w-16" />

        <div className="text-bios-title">
          {indicators && (
            <p className="text-4xl 2xl:text-5xl">
              {indicators.geral.conservadores}
            </p>
          )}
          {!indicators && <Skeleton className="mx-auto size-12" />}
          <span className="text-sm">conservadores</span>
        </div>

        <Separator.Root className="bg-bios-primary mx-3 my-6 h-px w-1/2 md:mb-auto md:w-12 lg:w-16" />

        <div className="text-bios-title">
          {indicators && (
            <p className="text-4xl 2xl:text-5xl">
              {indicators.geral.total_area_preservada}
            </p>
          )}
          {!indicators && <Skeleton className="mx-auto size-12" />}
          <span className="text-sm">áreas preservadas (ha)</span>
        </div>
      </div>

      <img
        src={circleBird}
        alt=""
        className="absolute hidden w-52 lg:top-12 lg:right-8 xl:block 2xl:top-10 2xl:right-36 2xl:w-64"
      />
      <img
        src={cloud}
        alt=""
        className="absolute top-72 right-16 hidden w-64 translate-x-1/2 xl:block"
      />
      <img
        src={personLemonCloud}
        alt=""
        className="absolute -top-1 hidden lg:left-6 lg:w-60 xl:block 2xl:left-16 2xl:w-72"
      />

      {/* Seção - Nossos indicadores por tipo */}
      <div className="pt-10 lg:px-6 lg:pt-0">
        <div className="flex flex-col flex-wrap items-center justify-center gap-5 lg:flex-row lg:gap-14">
          {isLoading ? (
            <Skeleton quantity={4} className="h-96 rounded-2xl" />
          ) : (
            indicators &&
            Object.entries(indicators)
              .filter(
                ([key, value]) =>
                  key !== 'geral' && Object.values(value).some((val) => val),
              )
              .map(([key, value]) => {
                const typedKey = key as keyof IndicatorsKeysWithoutGeral
                return (
                  <IndicatorsCard
                    key={typedKey}
                    title={titleMap[typedKey]}
                    urlImage={imageUrl[typedKey]}
                    quantityOfProjects={value.projetos}
                    quantityOfConservatives={value.conservadores}
                    quantityOfPreservedArea={value.total_area_preservada}
                  />
                )
              })
          )}
        </div>
      </div>
      {/* Seção - Nossos indicadores por tipo */}

      {/* Seção - Conheça nossas áreas de conservação */}
      <div className="pt-10 lg:pt-0 xl:pt-10">
        <h1 className="text-bios-700 mb-10 text-center text-2xl font-bold md:text-4xl 2xl:text-5xl">
          Conheça nossas áreas de conservação
        </h1>
        <p className="text-monochromatic-500 text-center text-xl font-medium 2xl:text-2xl">
          O Brasil é o maior detentor de biodiversidade do planeta com seis
          grandes
          <br />
          <span>
            biomas distintos totalizando mais de 850 milhões de hectares.
          </span>
        </p>
      </div>
    </div>
  )
}
